import { user as userService } from '../../services';

export const ActionTypes = {
  SET_USER_DATA: 'SET_USER_DATA',
  SET_QUERIED_ADDRESS: 'SET_QUERIED_ADDRESS',
  SET_USER_SEEN_ADDRESS_PROMPT: 'SET_USER_SEEN_ADDRESS_PROMPT',
  API_ERROR: 'API_ERROR',
};

/**
 * @description action creator for logging user in
 * @param {String} email user email
 * @param {String} password user password (plain text)
 * @param {Function} [onSuccess = () => {}] callback for when request is successful
 * @param {Function} [onError = () => {}] callback for when request fails
 */
export const login = (email, password, onSuccess = () => {}, onError = () => {}) => {
  return async (dispatch) => {
    try {
      const { user } = await userService.login(email, password);
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: user });
      onSuccess();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'LOGIN',
          error,
        },
      });
      onError(error);
    }
  };
};

/**
 * @description action creator for signing user up
 * @param {String} email user email
 * @param {String} password user password (plain text)
 * @param {String} firstName user first name
 * @param {String} lastName user last name
 * @param {Function} [onSuccess = () => {}] callback for when request is successful
 * @param {Function} [onError = () => {}] callback for when request fails
 */
export const signUp = (email, password, firstName, lastName, onSuccess = () => {}, onError = () => {}) => {
  return async (dispatch) => {
    try {
      const { user } = await userService.signUp(email, password, firstName, lastName);
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: user });
      onSuccess();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'SIGN UP',
          error,
        },
      });
      onError(error);
    }
  };
};

/**
 * @description action creator for logging user in from local storage
 */
export const getUserFromStorage = () => {
  return async (dispatch) => {
    try {
      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'LOGIN FROM STORAGE',
          error,
        },
      });
    }
  };
};

/**
 * @description action creator for saving an election to the user
 */
export const saveElectionToUser = (electionId) => {
  return async (dispatch) => {
    try {
      await userService.saveElection(electionId);
      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'LOGIN FROM STORAGE',
          error,
        },
      });
    }
  };
};

/**
 * @description action creator for removing an election from the user
 */
export const removeElectionFromUser = (electionId) => {
  return async (dispatch) => {
    try {
      await userService.removeElection(electionId);
      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'LOGIN FROM STORAGE',
          error,
        },
      });
    }
  };
};


/**
 * @description action creator for updating a user
 */
export const updateUser = (fields) => {
  return async (dispatch) => {
    try {
      await userService.updateUser(fields);
      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'UPDATE_USER',
          error,
        },
      });
    }
  };
};

/**
 * @description action creator for setting user quiz results for natl topics
 */
export const setUserNatlTopicQuizScores = (scores, onSuccess = () => {}, onFailure = () => {}) => {
  return async (dispatch) => {
    try {
      await userService.setUserNatlTopicQuizScores(scores);
      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
      onSuccess();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'UPDATE_USER',
          error,
        },
      });
      onFailure(error);
    }
  };
};

/**
 * @description action creator for setting user quiz results for natl topics
 */
export const setUserRaceQuizScores = (raceId, scores, onSuccess = () => {}, onFailure = () => {}) => {
  return async (dispatch) => {
    try {
      await userService.setUserRaceQuizScores(raceId, scores);
      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
      onSuccess();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'UPDATE_USER',
          error,
        },
      });
      onFailure(error);
    }
  };
};

/**
 * @description action creator for setting user quiz results for natl and race-specific topics
 */
export const setUserQuizScores = (natlScores, quizScores, raceId, onSuccess = () => {}, onFailure = () => {}) => {
  return async (dispatch) => {
    try {
      if (Object.keys(natlScores).length > 0) {
        await userService.setUserNatlTopicQuizScores(natlScores);
      }

      if (Object.keys(quizScores).length > 0 && raceId) {
        await userService.setUserRaceQuizScores(raceId, quizScores);
      }

      const response = await userService.getUserFromStorage();
      dispatch({ type: ActionTypes.SET_USER_DATA, payload: response });
      onSuccess();
    } catch (error) {
      dispatch({
        type: ActionTypes.API_ERROR,
        payload: {
          action: 'UPDATE_USER',
          error,
        },
      });
      onFailure(error);
    }
  };
};

/**
 * @description action creator for signing user out
 */
export const signOut = () => {
  return (dispatch) => {
    userService.signOut();
    dispatch({ type: ActionTypes.SET_USER_DATA, payload: {} });
  };
};

/**
 * @description action creator for setting queried address
 */
export const setQueriedAddress = (address) => {
  return (dispatch) => {
    dispatch({ type: ActionTypes.SET_QUERIED_ADDRESS, payload: address });
  };
};

/**
 * @description action creator for setting queried address
 */
export const setUserSeenAddressPrompt = (value) => {
  return (dispatch) => {
    dispatch({ type: ActionTypes.SET_USER_SEEN_ADDRESS_PROMPT, payload: value });
  };
};
